// Option-list construction for the timezone picker (TTT-67). Pure — no React, no DOM beyond
// localStorage — so the search/grouping/recents rules are unit-testable on their own.
//
// Zones are never collapsed by offset: Asia/Tokyo, Asia/Seoul and Pacific/Palau all read
// "(GMT+09:00)" today but have different DST rules, so merging them would silently break
// Brisbane vs Melbourne in the southern summer.

import { tzOffsetLabel, tzOffsetMinutes } from './timezone';

const FALLBACK_ZONES = [
  'Pacific/Honolulu', 'America/Los_Angeles', 'America/New_York', 'America/Sao_Paulo',
  'Europe/London', 'Europe/Paris', 'Africa/Cairo', 'Asia/Dubai', 'Asia/Kolkata',
  'Asia/Bangkok', 'Asia/Manila', 'Asia/Shanghai', 'Asia/Singapore', 'Asia/Tokyo',
  'Asia/Seoul', 'Australia/Sydney', 'Pacific/Auckland', 'UTC',
];

export const RECENTS_KEY = 'ropida.tz.recents';
const RECENTS_MAX = 3;

export function allZones() {
  try {
    if (typeof Intl.supportedValuesOf === 'function') return Intl.supportedValuesOf('timeZone');
  } catch { /* not supported — fall through */ }
  return FALLBACK_ZONES;
}

// One option per zone: display label, plus a lowercased haystack so "tok" finds Asia/Tokyo.
// Offsets are DST-dependent but stable for the lifetime of an open picker — computed once.
export function buildZoneOptions(now = new Date()) {
  return allZones()
    .map((tz) => {
      const name = tz.replace(/_/g, ' ');
      const offsetLabel = tzOffsetLabel(tz, now);
      return {
        tz,
        name,
        offsetLabel,
        label: `(${offsetLabel}) ${name}`,
        offset: tzOffsetMinutes(tz, now),
        search: `(${offsetLabel}) ${name}`.toLowerCase(),
      };
    })
    .sort((a, b) => a.offset - b.offset || a.tz.localeCompare(b.tz));
}

export function filterZones(options, query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return options;
  return options.filter((o) => o.search.includes(q));
}

// Downshift needs ONE flat array to do index math over, so groups can't be nested arrays.
// Returns that flat list plus `headers`: the index each group label is rendered above.
// The detected zone deliberately appears twice — pinned on top and again under its region —
// so searching for your own zone finds it where you'd expect.
export function groupZones({ options, detected, recents = [], selected, query = '' }) {
  const filtered = filterZones(options, query);
  const byTz = new Map(filtered.map((o) => [o.tz, o]));

  const items = [];
  const headers = {};
  const pushGroup = (label, group) => {
    if (!group.length) return;
    headers[items.length] = label;
    items.push(...group);
  };

  const detectedOption = detected ? byTz.get(detected) : undefined;
  pushGroup('Detected', detectedOption ? [detectedOption] : []);

  // Already-visible zones would just render twice at the top of the list.
  pushGroup('Recently used', recents
    .filter((tz) => tz !== detected && tz !== selected)
    .map((tz) => byTz.get(tz))
    .filter(Boolean));

  pushGroup('All timezones', filtered);

  return { items, headers };
}

// Recents are a convenience: a browser that refuses localStorage (Safari private mode, quota,
// disabled cookies) degrades to an empty list and never breaks the picker.
export function readRecents(knownZones) {
  try {
    const raw = JSON.parse(window.localStorage.getItem(RECENTS_KEY) || '[]');
    if (!Array.isArray(raw)) return [];
    return raw
      .filter((tz) => typeof tz === 'string' && (!knownZones || knownZones.has(tz)))
      .slice(0, RECENTS_MAX);
  } catch {
    return [];
  }
}

export function pushRecent(tz) {
  try {
    const raw = JSON.parse(window.localStorage.getItem(RECENTS_KEY) || '[]');
    const rest = Array.isArray(raw) ? raw.filter((x) => typeof x === 'string' && x !== tz) : [];
    window.localStorage.setItem(RECENTS_KEY, JSON.stringify([tz, ...rest].slice(0, RECENTS_MAX)));
  } catch {
    /* see readRecents — never fail a pick over a storage problem */
  }
}
